import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../utills/axiosInstance";
import "./Template.css";

const Templates = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const candidateData = location.state?.candidateData;
    const profile = useMemo(() => JSON.parse(localStorage.getItem('profile') || '{}'), []);

    const [templates, setTemplates] = useState([]);
    const [newTemplate, setNewTemplate] = useState({ name: '', sections: [] });
    const [loading, setLoading] = useState(false);

    const fetchTemplates = async () => {
        try {
            const res = await axiosInstance.get('/api/template');
            setTemplates(res.data || []);
        } catch (error) {
            console.error("Error fetching templates:", error);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const buildPdfData = useCallback((candData) => ({
        firstName: candData.firstName,
        lastName: candData.lastName,
        fullName: `${candData.firstName} ${candData.lastName}`,
        designation: candData.designation,
        location: candData.location,
        presentAddress: candData.presentAddress,
        workPhoneNumber: candData.workPhoneNumber,
        dateOfJoining: candData.dateOfJoining,
        currentDate: new Date().toLocaleDateString(),
        joinDate: new Date(candData.dateOfJoining).toLocaleDateString(),
        hrName: `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
        hrMail: profile.email || '',
    }), [profile]);

    const handleSectionChange = useCallback((sectionIndex, field, value) => {
        setNewTemplate((prev) => {
            const newSections = [...prev.sections];
            if (field === 'heading') {
                newSections[sectionIndex].heading = value;
            } else if (field.startsWith('paragraph-')) {
                const paraIndex = parseInt(field.split('-')[1]);
                newSections[sectionIndex].paragraphs[paraIndex] = value;
            }
            return { ...prev, sections: newSections };
        });
    }, []);

    const handleAddSection = () => {
        setNewTemplate((prev) => ({
            ...prev,
            sections: [...prev.sections, { heading: '', paragraphs: [''] }],
        }));
    };

    const handleRemoveSection = (sectionIndex) => {
        setNewTemplate((prev) => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== sectionIndex),
        }));
    };

    const handleAddParagraph = (sectionIndex) => {
        setNewTemplate((prev) => {
            const newSections = [...prev.sections];
            newSections[sectionIndex].paragraphs.push('');
            return { ...prev, sections: newSections };
        });
    };

    const handleRemoveParagraph = (sectionIndex, paraIndex) => {
        setNewTemplate((prev) => {
            const newSections = [...prev.sections];
            newSections[sectionIndex].paragraphs = newSections[sectionIndex].paragraphs.filter((_, i) => i !== paraIndex);
            if (newSections[sectionIndex].paragraphs.length === 0) {
                newSections[sectionIndex].paragraphs = [''];
            }
            return { ...prev, sections: newSections };
        });
    };

    const handleSaveNewTemplate = async () => {
        if (!newTemplate.name.trim()) {
            alert("Template name is required.");
            return;
        }
        if (newTemplate.sections.length === 0 || newTemplate.sections.every(sec => sec.paragraphs.every(p => !p.trim()))) {
            alert("At least one section with non-empty paragraphs is required.");
            return;
        }

        setLoading(true);
        const saveData = {
            name: newTemplate.name.trim(),
            sections: newTemplate.sections.map(sec => ({
                heading: sec.heading.trim() || undefined,
                paragraphs: sec.paragraphs.filter(p => p.trim())
            })).filter(sec => sec.paragraphs.length > 0)
        };

        try {
            const res = await axiosInstance.post("/api/template", saveData);
            const template = res.data;
            navigate(`/template/${template._id}`, { state: { candidateData } });
        } catch (error) {
            console.error("Error creating template:", error);
            alert("Error creating template. Please try again.");
        } finally {
            setLoading(false);
            setNewTemplate({ name: '', sections: [] });
        }
    };

    const handleSelectTemplate = (template) => {
        navigate(`/template/${template._id}`, { state: { candidateData } });
    };

    return (
        <div className="templates-container">
            <div className="header-section">
                <h2 className="page-title">Offer Letter Templates</h2>
                {candidateData && (
                    <div className="alert alert-info">
                        Candidate saved successfully! Select an existing template or create a new one to generate the offer letter.
                    </div>
                )}
            </div>

            <section className="create-section">
                <h3>Create New Template</h3>
                <div className="form-group">
                    <label htmlFor="templateName">Template Name <span className="required">*</span></label>
                    <input
                        type="text"
                        id="templateName"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate((prev) => ({ ...prev, name: e.target.value }))}
                        className="form-control"
                        placeholder="Enter template name"
                    />
                </div>
                <h4>Sections (Add headings and paragraphs; headings optional)</h4>

                {newTemplate.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="section-block">
                        <div className="form-group">
                            <label>Section {sectionIndex + 1} Heading (optional)</label>
                            <input
                                type="text"
                                value={section.heading}
                                onChange={(e) => handleSectionChange(sectionIndex, 'heading', e.target.value)}
                                className="form-control"
                                placeholder="e.g., Work Hours"
                            />
                        </div>
                        {section.paragraphs.map((para, paraIndex) => (
                            <div key={paraIndex} className="form-group">
                                <label>Paragraph {paraIndex + 1}</label>
                                <textarea
                                    value={para}
                                    onChange={(e) => handleSectionChange(sectionIndex, `paragraph-${paraIndex}`, e.target.value)}
                                    rows={3}
                                    className="form-control"
                                    placeholder="Enter paragraph text (use {placeholder} like {designation})"
                                />
                                {section.paragraphs.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => handleRemoveParagraph(sectionIndex, paraIndex)}
                                    >
                                        Remove Paragraph
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleAddParagraph(sectionIndex)}
                        >
                            Add Paragraph
                        </button>
                        {newTemplate.sections.length > 1 && (
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRemoveSection(sectionIndex)}
                            >
                                Remove Section
                            </button>
                        )}
                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleAddSection}
                >
                    Add Section
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveNewTemplate}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : candidateData ? 'Save and Generate' : 'Save Template'}
                </button>
            </section>

            <section className="list-section">
                <h3>Existing Templates</h3>
                {templates.length === 0 ? (
                    <p>No templates available. Create one above.</p>
                ) : (
                    <div className="templates-list">
                        {templates.map((template) => (
                            <div key={template._id} className="template-card">
                                <h4>{template.name}</h4>
                                <p>{template.sections.length} sections</p>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleSelectTemplate(template)}
                                >
                                    {candidateData ? 'Select & Generate' : 'View Preview'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Templates;
